/**
 * Sentrale statusgrupper for tilbudsutsnitt («view»).
 *
 * Brukes både av dashboardets KPI-kort (telling + lenke) og av
 * tilbudslisten (URL-basert filter), slik at tallet på dashboardet og
 * innholdet i listen aldri kan sprike.
 *
 * Ren modul uten I/O.
 */

import type { QuoteStatus } from "@/lib/calc";

export const QUOTE_VIEWS = [
  "open",
  "approved",
  "sent",
  "won",
  "won-30d",
  "in-progress",
  "waiting",
  "closed",
  "all",
] as const;
export type QuoteView = (typeof QUOTE_VIEWS)[number];

/**
 * Filtervalgene som vises som faner i tilbudslisten.
 * Rekkefølgen er bevisst: slik jobben faktisk beveger seg.
 */
export const QUOTE_LIST_TABS: QuoteView[] = ["all", "in-progress", "waiting", "approved", "closed"];

/** Åpne tilbud: alt som fortsatt er i spill hos oss eller hos kunden. */
export const OPEN_STATUSES: QuoteStatus[] = ["draft", "ready_to_send", "sent", "opened"];
/** Sendt og venter svar fra kunde. */
export const SENT_STATUSES: QuoteStatus[] = ["sent", "opened"];
/** Godkjent (vunnet) omsetning. */
export const WON_STATUSES: QuoteStatus[] = ["approved", "completed", "invoiced"];
/** Kun godkjente. */
export const APPROVED_STATUSES: QuoteStatus[] = ["approved"];
/** Under arbeid hos oss: ikke sendt til kunde ennå. */
export const IN_PROGRESS_STATUSES: QuoteStatus[] = ["draft", "ready_to_send"];
/** Venter på svar: sendt, og åpnet av kunde teller fortsatt som venting. */
export const WAITING_STATUSES: QuoteStatus[] = ["sent", "opened"];
/** Avsluttet: ferdig på godt eller vondt. */
export const CLOSED_STATUSES: QuoteStatus[] = [
  "completed",
  "invoiced",
  "rejected",
  "expired",
  "archived",
];

const VIEW_STATUSES: Record<Exclude<QuoteView, "all">, QuoteStatus[]> = {
  open: OPEN_STATUSES,
  approved: APPROVED_STATUSES,
  sent: SENT_STATUSES,
  won: WON_STATUSES,
  "won-30d": WON_STATUSES,
  "in-progress": IN_PROGRESS_STATUSES,
  waiting: WAITING_STATUSES,
  closed: CLOSED_STATUSES,
};

export const QUOTE_VIEW_LABELS: Record<QuoteView, string> = {
  open: "Åpne tilbud",
  approved: "Godkjent",
  sent: "Sendt (venter svar)",
  won: "Omsetning (godkjent)",
  "won-30d": "Omsetning siste 30 dager",
  "in-progress": "Under arbeid",
  waiting: "Venter på svar",
  closed: "Avsluttet",
  all: "Alle",
};

export function isQuoteView(value: unknown): value is QuoteView {
  return typeof value === "string" && (QUOTE_VIEWS as readonly string[]).includes(value);
}

/** Normaliser ukjent verdi fra URL til et gyldig utsnitt. */
export function parseQuoteView(value: unknown): QuoteView {
  return isQuoteView(value) ? value : "all";
}

/** Statuser som inngår i utsnittet. Tom liste = ingen statusbegrensning. */
export function statusesForView(view: QuoteView): QuoteStatus[] {
  if (view === "all") return [];
  return VIEW_STATUSES[view];
}

export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

type QuoteLike = {
  status?: string | null;
  approved_at?: string | null;
  created_at?: string | null;
};

/** Er tilbudet med i utsnittet? `now` gjør 30-dagersvinduet testbart. */
export function matchesQuoteView(quote: QuoteLike, view: QuoteView, now = Date.now()): boolean {
  const statuses = statusesForView(view);
  if (statuses.length > 0 && !statuses.includes(quote.status as QuoteStatus)) return false;
  if (view === "won-30d") {
    const ref = quote.approved_at ?? quote.created_at;
    if (!ref) return false;
    const t = new Date(ref).getTime();
    if (Number.isNaN(t) || t < now - THIRTY_DAYS_MS) return false;
  }
  return true;
}
